"use client";

import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";

const REFRESH_DEBOUNCE_MS = 400;

/**
 * Subscribes to Strapi-driven SSE (`/api/cms-events`).
 * When a Strapi webhook hits `/api/revalidate`, connected browsers soft-refresh.
 * Debounced so seed / bulk CMS writes don't thrash `router.refresh()`.
 */
export function CmsLiveReload() {
  const router = useRouter();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;

    const source = new EventSource("/api/cms-events");

    source.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as { type?: string };
        if (data.type !== "cms-change") return;

        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          router.refresh();
        }, REFRESH_DEBOUNCE_MS);
      } catch {
        // ignore malformed events
      }
    };

    source.onerror = () => {
      // Browser will retry EventSource automatically
    };

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      source.close();
    };
  }, [router]);

  return null;
}
